import React, { useMemo, useState } from "react";
import Image from "next/image";
import moment from "moment";
import { MDBCol, MDBRow } from "mdb-react-ui-kit";
import Btn from "@/components/common/Btn";
import {
    subTitleDeteleteBooking,
    titleDeteleteBooking,
} from "@/utils/mockData/bookingMocks";
import { checkRating } from "@/utils/common";
import CustomModal from "@/components/common/CustomModal";
import styles from "../../../styles/booking/cardBooking.module.scss";
import {
    EActiveInvoiceStatus,
    EBtnType,
    EButtonBooking,
} from "../../../models/common/enums";
import { BookingReadableTO } from "../../../models/detail";
import DeleteBookingModalBody from "./DeleteBookingModalBody";

type DisabledProps = {
    content: BookingReadableTO;
    onClick: (
        titleContent: string,
        buttonId: EButtonBooking,
        tourId: string
    ) => void;
};

const CardBooking: React.FunctionComponent<DisabledProps> = ({
    content,
    onClick,
}) => {
    const [showModal, setShowModal] = useState(false);

    const isEnabled = useMemo(
        () =>
            moment().isBefore(moment(content?.startdate)) &&
            Math.abs(moment().diff(moment(content?.startdate), "days")) >=
                content.cancellationPolicy,
        [content]
    );

    const getTravellerString = (
        numberOfPeople: number,
        singularLabel: string,
        pluralLabel: string
    ) => {
        if (!numberOfPeople) {
            return "";
        }

        return `${numberOfPeople} ${
            numberOfPeople > 1 ? pluralLabel : singularLabel
        }`;
    };

    return (
        <MDBRow className="mb-5">
            <MDBCol
                size={12}
                lg={7}
                className="pt-0 pt-sm-3 order-2 order-md-1 position-relative"
            >
                <MDBRow>
                    <p className={styles.title}>{content?.title}</p>
                </MDBRow>
                {!!content?.reviews && content?.reviews !== "" && (
                    <MDBRow>
                        <div className={styles.line}>
                            {checkRating(content?.rating) && (
                                <>
                                    <Image
                                        src="/images/star.svg"
                                        width={20}
                                        height={20}
                                        alt={content?.title}
                                    />
                                    <span className={styles.rating}>
                                        {Number(content.rating)}
                                    </span>
                                </>
                            )}

                            {!!+content.reviews && (
                                <span>({content?.reviews} reviews)</span>
                            )}
                        </div>
                    </MDBRow>
                )}
                <MDBRow>
                    <MDBCol size={12} lg={6}>
                        <MDBRow>
                            <div className={styles.line}>
                                <Image
                                    src="/images/people.svg"
                                    width={20}
                                    height={20}
                                    alt="Highlights of Sorrento & its surroundings"
                                />
                                <span style={{ textTransform: "capitalize" }}>
                                    {` ${getTravellerString(
                                        content.adults,
                                        "adult",
                                        "adults"
                                    )} ${
                                        content.children ? "," : ""
                                    } ${getTravellerString(
                                        content.children,
                                        "child",
                                        "children"
                                    )}`}
                                </span>
                            </div>
                        </MDBRow>
                        <MDBRow>
                            <div className={styles.line}>
                                <Image
                                    src="/images/calendar.svg"
                                    width={20}
                                    height={20}
                                    alt="Highlights of Sorrento & its surroundings"
                                />
                                <span>
                                    {moment(content?.startdate).format(
                                        "Do MMMM"
                                    )}{" "}
                                    -{" "}
                                    {moment(content?.endDate).format("Do MMMM")}
                                </span>
                            </div>
                        </MDBRow>
                    </MDBCol>
                    <MDBCol size={12} lg={6}>
                        <MDBRow>
                            <div className={styles.line}>
                                <Image
                                    src="/images/clock.svg"
                                    width={20}
                                    height={20}
                                    alt="Highlights of Sorrento & its surroundings"
                                />
                                <span>
                                    {content?.days} Days, {content?.night}{" "}
                                    nights
                                </span>
                            </div>
                        </MDBRow>
                        <MDBRow>
                            <div className={styles.line}>
                                <Image
                                    src="/images/flag.svg"
                                    width={20}
                                    height={20}
                                    alt="Highlights of Sorrento & its surroundings"
                                />
                                <span>
                                    {content?.destinations} Destinations
                                </span>
                            </div>
                        </MDBRow>
                    </MDBCol>
                </MDBRow>
                <MDBRow>
                    <MDBCol
                        size={12}
                        lg={6}
                        className="d-flex align-self-center justify-content-center justify-content-lg-start"
                    >
                        <Btn
                            className={styles.buttonBooking}
                            type="button"
                            btnType={EBtnType.TRANSPARENT}
                            onClick={() =>
                                onClick(
                                    "General Info",
                                    EButtonBooking.PERSONALINFO,
                                    // eslint-disable-next-line no-underscore-dangle
                                    content._id
                                )
                            }
                        >
                            Manage yor Booking
                        </Btn>
                    </MDBCol>
                    <MDBCol
                        size={12}
                        lg={6}
                        className="d-flex align-self-center justify-content-center justify-content-lg-start my-3 my-lg-0"
                    >
                        <div>
                            {content.activeInvoiceStatus ===
                            EActiveInvoiceStatus.REFUNDED ? (
                                <p className={styles.refunded}>refunded</p>
                            ) : (
                                <Btn
                                    type="button"
                                    btnType={EBtnType.TRANSPARENT}
                                    className={
                                        isEnabled
                                            ? styles.buttonDelete
                                            : styles.buttonDeletecancellationPolicy
                                    }
                                    onClick={() => setShowModal(true)}
                                    disabled={!isEnabled}
                                >
                                    x Delete
                                </Btn>
                            )}
                        </div>
                    </MDBCol>
                </MDBRow>
            </MDBCol>
            <MDBCol
                size={12}
                lg={5}
                className="d-flex align-self-center justify-content-center order-1 order-md-2"
            >
                <Image
                    src={content.image}
                    width={300}
                    height={300}
                    alt={content?.title}
                    className={styles.image}
                />
            </MDBCol>
            <CustomModal
                displayModal={showModal}
                setDisplayModal={() => setShowModal(false)}
                sectionTitle={{
                    title: titleDeteleteBooking,
                    subTitle: subTitleDeteleteBooking,
                }}
            >
                <DeleteBookingModalBody
                    transactionId={content.orderTransactionId}
                    // eslint-disable-next-line no-underscore-dangle
                    orderId={content._id}
                    onSuccessAfterDelete={() => setShowModal(false)}
                />
            </CustomModal>
        </MDBRow>
    );
};

export default CardBooking;
